import { useCallback, useEffect, useState } from 'react';

/**
 * useHomeLang — Iter-52.17
 *
 * Home-screen-only Hindi/English toggle. Persists the parent's
 * preference in `localStorage` under `rb_home_lang` (values: 'en' |
 * 'hi'). Default is 'en' on first load per product spec.
 *
 * Scope is deliberately narrow: this hook powers the Home screen
 * ONLY. Other tabs (Books, Dashboard, Profiles, Reading, etc.) stay
 * English — that's what the parent chose after a discussion in the
 * iter-52.17 brief. Expansion to other tabs will happen one at a
 * time later.
 *
 * The `hint()` helper returns a short instruction ALWAYS in the
 * OTHER language, so a parent who can't read the current language
 * still sees a call-to-action to switch. This is the "dual hint"
 * requirement — a Hindi-speaking parent who lands on the English
 * default sees "हिंदी में देखने के लिए यहाँ दबाएं"; and vice-versa.
 */
export const HOME_LANG_KEY = 'rb_home_lang';

// Central copy dictionary. `en` is the source of truth (already lives
// on-screen today); `hi` is a warm, natural Devanagari rendering — not
// a mechanical Google-translate output. Keep every key present in both
// blocks so `homeT()` never falls back to the raw key.
export const HOME_STRINGS = {
  en: {
    hi_greeting: 'Hi',
    total_stars_label: 'Total stars',
    streak_label: 'Day streak',

    guides_title: 'Need help? Guides & Info',
    guides_subtitle: 'English & Hindi walk-throughs on your Profile — always here when you need them',

    starter_heading: 'Start here',
    starter_subtitle: 'Free reading passages — Hindi & English',
    starter_picked_prefix: 'Picked for',
    starter_picked_for_you: 'Picked for you',
    starter_level_label: 'Level',
    starter_see_all: 'See all passages',
    starter_loading: 'Loading…',
    starter_empty: 'The library is empty right now — check back soon.',

    btb_eyebrow: 'Beat the Bot',
    btb_title: 'Take on a bot right now',
    btb_subtitle: "Quick MCQ challenge on any book you've read",

    continue_reading: 'Continue reading',
    start_reading: 'Start reading',
    no_books: 'No books yet. Tap "Add a new book" to snap textbook pages.',
    add_book: 'Add a new book',

    quick_practice: 'Quick practice',
    quick_practice_sub: 'Quick single page',
    all_my_books: 'All my books',
    all_my_books_sub: 'Open library',

    picker_title: 'Pick a book to quiz on',
    picker_subtitle: 'The bots will draw questions from the passage you read most recently.',
    picker_pages_suffix: 'pages',
    picker_untitled: 'Untitled book',

    toggle_hint_for_other: 'हिंदी में देखने के लिए यहाँ दबाएं',
    toggle_switch_to_hi: 'हिंदी',
    toggle_switch_to_en: 'EN',
  },
  hi: {
    hi_greeting: 'नमस्ते',
    total_stars_label: 'कुल सितारे',
    streak_label: 'दिनों की स्ट्रीक',

    guides_title: 'मदद चाहिए? गाइड और जानकारी',
    guides_subtitle: 'हिंदी और अंग्रेज़ी दोनों में स्टेप-बाय-स्टेप गाइड — Profile पेज पर हमेशा उपलब्ध',

    starter_heading: 'यहाँ से शुरू करें',
    starter_subtitle: 'मुफ़्त पढ़ने के पाठ — हिंदी और अंग्रेज़ी',
    starter_picked_prefix: 'इनके लिए चुना',
    starter_picked_for_you: 'आपके लिए चुना गया',
    starter_level_label: 'लेवल',
    starter_see_all: 'सारे पाठ देखें',
    starter_loading: 'लोड हो रहा है…',
    starter_empty: 'अभी कोई पाठ उपलब्ध नहीं है — जल्द ही जोड़े जाएँगे।',

    btb_eyebrow: 'बॉट को हराओ',
    btb_title: 'अभी बॉट के साथ खेलो',
    btb_subtitle: 'पढ़ी हुई किसी भी किताब पर तेज़ MCQ चुनौती',

    continue_reading: 'पढ़ना जारी रखें',
    start_reading: 'पढ़ना शुरू करें',
    no_books: 'अभी कोई किताब नहीं है। "नई किताब जोड़ें" दबाकर किताब की तस्वीर लो।',
    add_book: 'नई किताब जोड़ें',

    quick_practice: 'त्वरित अभ्यास',
    quick_practice_sub: 'एक पेज का अभ्यास',
    all_my_books: 'मेरी सारी किताबें',
    all_my_books_sub: 'लाइब्रेरी खोलें',

    picker_title: 'क्विज़ के लिए किताब चुनें',
    picker_subtitle: 'बॉट आपकी सबसे हाल की पढ़ी हुई किताब से सवाल पूछेंगे।',
    picker_pages_suffix: 'पेज',
    picker_untitled: 'बिना नाम की किताब',

    toggle_hint_for_other: 'Tap here to switch to English',
    toggle_switch_to_hi: 'हिंदी',
    toggle_switch_to_en: 'EN',
  },
};

function readInitialLang() {
  try {
    const v = localStorage.getItem(HOME_LANG_KEY);
    if (v === 'hi' || v === 'en') return v;
  } catch {}
  return 'en';
}

export function useHomeLang() {
  const [lang, setLangState] = useState(readInitialLang);

  useEffect(() => {
    try { localStorage.setItem(HOME_LANG_KEY, lang); } catch {}
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === 'hi' || next === 'en') setLangState(next);
  }, []);

  const toggle = useCallback(() => {
    setLangState((prev) => (prev === 'hi' ? 'en' : 'hi'));
  }, []);

  const t = useCallback((key) => {
    const block = HOME_STRINGS[lang] || HOME_STRINGS.en;
    return block[key] ?? HOME_STRINGS.en[key] ?? key;
  }, [lang]);

  // The value stored in each language block's `toggle_hint_for_other`
  // is INTENTIONALLY the copy in the OTHER language — the whole point
  // is to speak TO the parent who can't read the current UI language.
  // So the lookup is on the CURRENT `lang`, not the other one.
  const hint = useCallback(() => {
    const block = HOME_STRINGS[lang] || HOME_STRINGS.en;
    return block.toggle_hint_for_other;
  }, [lang]);

  return { lang, setLang, toggle, t, hint };
}
